import {
  DEFAULT_TIME_ZONE,
  RESIDENT_STATE,
  UniversalError,
  isFacilityKey,
  t as tt,
} from "@packages/core";
import {
  ErrorComponentProps,
  Link,
  NotFoundRouteProps,
  Outlet,
  createRootRoute,
  useNavigate,
  useParams,
  useRouter,
} from "@tanstack/react-router";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import * as semver from "semver";
import { toast } from "sonner";
import { Temporal } from "temporal-polyfill";

import { ButtonStyle, PaperStyle, Reha30LogoA, TypographyStyle, showError } from "#components";
import {
  TANSTACK_QUERY_CLIENT,
  getNativeApplicationEdition,
  getPushNotificationReceiverType,
  isNativeApplication,
  registerInstallation,
  subscribeData,
  subscribePushNotifications,
  useApplicationConfigurationQuery,
  useExternalNavigationRequest,
  useNativeApplicationIdentifierQuery,
  useNativeApplicationRevisionQuery,
  useNativeApplicationVersionQuery,
  usePushNotificationTokenQuery,
  useSocket,
  useUsername,
} from "#services";
import { css, cx } from "#styled-system/css";

export const Route = createRootRoute({
  component: Component,
  errorComponent: ErrorComponent,
  notFoundComponent: NotFoundComponent,
});

function Component() {
  const { t } = useTranslation();
  const reloadedAtKey = "Reha30.ReloadedAt";

  const applicationConfigurationQuery = useApplicationConfigurationQuery();
  const { data: applicationConfiguration } = applicationConfigurationQuery;
  const externalNavigationRequest = useExternalNavigationRequest();
  const navigate = useNavigate();
  const socket = useSocket();
  const username = useUsername();
  const nativeApplicationIdentifierQuery = useNativeApplicationIdentifierQuery();
  const nativeApplicationRevisionQuery = useNativeApplicationRevisionQuery();
  const nativeApplicationVersionQuery = useNativeApplicationVersionQuery();
  const { data: pushNotificationToken } = usePushNotificationTokenQuery();
  const { facilityKey } = useParams({ strict: false });

  useEffect(
    function () {
      if (
        !isNativeApplication() ||
        !nativeApplicationIdentifierQuery.data ||
        !nativeApplicationRevisionQuery.data ||
        !nativeApplicationVersionQuery.data
      ) {
        // Android 1.0.1／iOS 1.0.2以前のバージョンは、
        // インストール情報識別子の取得に対応していないため、
        // 省略する。
        return;
      }

      registerInstallation({
        edition: getNativeApplicationEdition(),
        facilityKey,
        identifier: nativeApplicationIdentifierQuery.data,
        revision: nativeApplicationRevisionQuery.data,
        updatedAt: Temporal.Now.plainDateTimeISO(DEFAULT_TIME_ZONE),
        version: nativeApplicationVersionQuery.data,
      }).catch(function (error) {
        console.error("インストール情報の更新に失敗しました。", error);
        toast.error(tt({ ja: "使用しているバージョンの情報をサーバーに送信出来ませんでした。" }));
      });
    },
    [
      facilityKey,
      nativeApplicationIdentifierQuery.data,
      nativeApplicationRevisionQuery.data,
      nativeApplicationVersionQuery.data,
      username,
    ],
  );

  const reloadedAt = useMemo(function () {
    try {
      const dateTime = window.localStorage.getItem(reloadedAtKey);
      return dateTime ? Temporal.PlainDateTime.from(dateTime) : undefined;
    } catch {
      return undefined;
    }
  }, []);

  const outdated = useMemo(
    function () {
      const requiredVersion = applicationConfiguration?.requiredVersion;
      if (!requiredVersion) {
        return false;
      }
      console.debug(
        "今のバージョン(" +
          NPM_PACKAGE_VERSION +
          ")と必要なバージョン(" +
          requiredVersion +
          ")を比較します。",
      );
      return semver.lt(NPM_PACKAGE_VERSION, requiredVersion);
    },
    [applicationConfiguration?.requiredVersion],
  );

  // アップデートが必要な場合は1分後に自動リロードを行う。
  useEffect(
    function () {
      if (outdated) {
        console.debug("バージョンが古いため、自動的にリロードされます。");
        const timer = setTimeout(
          function () {
            window.localStorage.setItem(
              reloadedAtKey,
              Temporal.Now.plainDateTimeISO("Asia/Tokyo").toString(),
            );
            window.location.reload();
          },
          reloadedAt
            ? Math.max(
                Math.floor(
                  reloadedAt
                    .add({ minutes: 1 })
                    .since(Temporal.Now.plainDateTimeISO("Asia/Tokyo"))
                    .total("milliseconds"),
                ),
                0,
              )
            : 0,
        );

        return function () {
          clearTimeout(timer);
        };
      }
    },
    [outdated, reloadedAt],
  );

  useEffect(
    function () {
      const type = getPushNotificationReceiverType();
      if (pushNotificationToken && type && username !== undefined) {
        subscribePushNotifications({
          // ログインしていない場合は施設を購読出来ないため、`null`で上書きする。
          // ログインしないままログインが必要なページに遷移した場合、
          // パスはそのままでログイン画面が表示される仕様であるため。
          facilityKey: username && isFacilityKey(facilityKey) ? facilityKey : null,
          subscribedAt: Temporal.Now.plainDateTimeISO(DEFAULT_TIME_ZONE),
          token: pushNotificationToken,
          type,
        })
          .then(function () {
            console.debug("プッシュ通知の購読に成功しました。");
          })
          .catch(function (error) {
            console.error("プッシュ通知の購読に失敗しました。", error);
            showError(error);
          });
      }
    },
    [pushNotificationToken, facilityKey, username],
  );

  useEffect(
    function () {
      if (socket && username !== undefined) {
        subscribeData(socket, {
          facilityKey: username && isFacilityKey(facilityKey) ? facilityKey : null,
        })
          .then(function () {
            console.debug("データの購読に成功しました。");
            return TANSTACK_QUERY_CLIENT.invalidateQueries(
              { refetchType: "active" },
              { cancelRefetch: false },
            );
          })
          .then(function () {
            console.debug("データの再取得に成功しました。");
          })
          .catch(function (error) {
            console.error("データの購読に失敗しました。", error);
            showError(error);
          });
      }
    },
    [facilityKey, socket, username],
  );

  useEffect(
    function () {
      const data = externalNavigationRequest?.data;
      if (data) {
        console.debug("外部ナビゲーションリクエストを処理します。", data);
        switch (data.type) {
          case RESIDENT_STATE.GOT_UP:
          case RESIDENT_STATE.WANDERING:
            console.debug(
              "施設入居者ページ(" + data.facilityKey + ", " + data.userKey + ")に遷移します。",
            );
            navigate({
              params: { facilityKey: data.facilityKey, userKey: data.userKey },
              to: "/facilities/$facilityKey/residents/$userKey",
            });
            break;
          default:
            console.warn(
              "不明な外部ナビゲーションリクエスト(" +
                (data satisfies never as any).type +
                ")です。",
              data,
            );
            break;
        }

        // 処理した後にデータをリセットする。
        useExternalNavigationRequest.setState({ data: null });
      } else {
        console.debug("外部ナビゲーションリクエストはありません。");
      }
    },
    [externalNavigationRequest?.data, navigate],
  );

  return (
    <Fragment>
      <div
        className={css({
          marginBottom: "env(safe-area-inset-bottom)",
          marginLeft: "env(safe-area-inset-left)",
          marginRight: "env(safe-area-inset-right)",
          marginTop: "env(safe-area-inset-top)",
          pointerEvents: "none",
          position: "fixed",
          zIndex: 65535,
        })}
      >
        <SocketErrorDisplay />
      </div>

      {outdated ? (
        <div
          className={css({
            backgroundColor: "#F7F5F2",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100dvh",
            minWidth: "100dvw",
          })}
        >
          <div
            className={css(PaperStyle.raw({ borderRadius: "12px", padding: "24px" }), {
              margin: "16px",
              maxWidth: "400px",
              width: "stretch",
            })}
          >
            <div className={css({ display: "flex", justifyContent: "center" })}>
              <Reha30LogoA className={css({ height: "32px", width: "auto" })} />
            </div>

            <h1
              className={TypographyStyle({
                ellipsis: true,
                fontSize: "18px",
                fontWeight: 600,
                marginTop: "24px",
                textAlign: "center",
              })}
            >
              {t("%Update.0%")}
            </h1>

            <p className={TypographyStyle({ fontSize: "14px", marginTop: "16px" })}>
              {t("%Update.1%")}
            </p>

            <div
              className={css({
                backgroundColor: "primary-foreground",
                borderRadius: "6px",
                marginTop: "16px",
                padding: "16px",
              })}
            >
              <h2
                className={TypographyStyle({
                  ellipsis: true,
                  fontSize: "14px",
                  fontWeight: 700,
                  lineHeight: 1,
                  textAlign: "center",
                })}
              >
                {t("%UpdateInformation.0%")}
              </h2>
              <div
                className={css(TypographyStyle.raw({ fontSize: "14px" }), {
                  display: "grid",
                  gridTemplateColumns: "auto auto",
                  gap: "4px",
                  marginTop: "8px",
                  justifyItems: "center",
                })}
              >
                <p className={TypographyStyle({ fontWeight: 500 })}>
                  {t("%CurrentVersion.0%")}&nbsp;:
                </p>
                <p className={TypographyStyle({ monospace: true })}>{NPM_PACKAGE_VERSION}</p>
                <p className={TypographyStyle({ fontWeight: 500 })}>
                  {t("%RequiredVersion.0%")}&nbsp;:
                </p>
                <p className={TypographyStyle({ monospace: true })}>
                  {applicationConfiguration?.requiredVersion}
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : applicationConfiguration?.maintenance ? (
        <div
          className={css({
            backgroundColor: "#F7F5F2",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100dvh",
            minWidth: "100dvw",
          })}
        >
          <div
            className={css(PaperStyle.raw({ borderRadius: "12px", padding: "24px" }), {
              margin: "16px",
              maxWidth: "400px",
              width: "stretch",
            })}
          >
            <div className={css({ display: "flex", justifyContent: "center" })}>
              <Reha30LogoA className={css({ height: "32px", width: "auto" })} />
            </div>

            <h1
              className={TypographyStyle({
                ellipsis: true,
                fontSize: "18px",
                fontWeight: 600,
                marginTop: "24px",
                textAlign: "center",
              })}
            >
              {t("%Maintenance.0%")}
            </h1>

            <p className={TypographyStyle({ fontSize: "14px", marginTop: "16px" })}>
              {t("%Maintenance.1%")}
            </p>
            <p className={TypographyStyle({ fontSize: "14px" })}>{t("%Maintenance.2%")}</p>

            {applicationConfiguration?.maintenancePeriod && (
              <div
                className={css({
                  backgroundColor: "primary-foreground",
                  borderRadius: "6px",
                  marginTop: "16px",
                  padding: "16px",
                })}
              >
                <h2
                  className={TypographyStyle({
                    ellipsis: true,
                    fontSize: "14px",
                    fontWeight: 700,
                    lineHeight: 1,
                    textAlign: "center",
                  })}
                >
                  {t("%MaintenancePeriod.0%")}
                </h2>
                <p
                  className={TypographyStyle({
                    fontSize: "14px",
                    marginTop: "8px",
                    textAlign: "center",
                  })}
                >
                  {t(
                    applicationConfiguration?.maintenancePeriod.at(1)
                      ? "%MaintenancePeriod.1%"
                      : "%MaintenancePeriod.2%",
                    {
                      $: {
                        a: applicationConfiguration?.maintenancePeriod.at(0),
                        b: applicationConfiguration?.maintenancePeriod.at(1),
                      },
                    },
                  )}
                </p>
              </div>
            )}

            <p className={TypographyStyle({ fontSize: "14px", marginTop: "16px" })}>
              {t("%Maintenance.3%")}
            </p>
            <p className={TypographyStyle({ fontSize: "14px" })}>{t("%Maintenance.4%")}</p>
          </div>
        </div>
      ) : (
        <Outlet />
      )}
      {/* <TanStackRouterDevtools /> */}
    </Fragment>
  );
}

function SocketErrorDisplay({ className, ...props }: React.ComponentProps<"p">) {
  const { t } = useTranslation();

  const socket = useSocket();
  const [open, setOpen] = useState(false);

  useEffect(
    function () {
      if (socket) {
        setOpen(false);
      } else {
        const timeout = setTimeout(function () {
          setOpen(true);
        }, 10_000);
        return function () {
          clearTimeout(timeout);
        };
      }
    },
    [socket],
  );

  return open ? (
    <p
      className={cx(
        css(TypographyStyle.raw({ fontSize: "14px", fontWeight: 700 }), { color: "red" }),
        className,
      )}
      {...props}
    >
      {t("%LostConnection.0%")}
    </p>
  ) : null;
}

function ErrorComponent({ error, reset, info }: ErrorComponentProps) {
  const router = useRouter();
  const { t } = useTranslation();

  useEffect(
    function () {
      if (info) {
        console.group("エラーが発生しました。");
        console.debug(info);
        console.groupEnd();
      }
    },
    [info],
  );

  useEffect(
    function () {
      if (error instanceof UniversalError) {
        showError(error);
      } else if (error.cause instanceof UniversalError) {
        showError(error.cause);
      } else {
        console.error(error);
      }
    },
    [error],
  );

  return (
    <div className={css({ display: "flex", flexDirection: "column", gap: "8px", padding: "16px" })}>
      <p>{t("%3%")}</p>

      <div
        className={css({
          alignItems: "center",
          display: "grid",
          gap: "8px",
          gridTemplateColumns: "repeat(auto-fit, minmax(0, 1fr))",
          marginTop: "16px",
        })}
      >
        <Link
          className={ButtonStyle({
            appearance: "contained",
            borderRadius: "4px",
            color: "primary",
            flexGrow: 1,
            padding: "10px 16px",
          })}
          to="/login"
        >
          {t("%BackToLoginPage.0%")}
        </Link>
        <button
          className={ButtonStyle({
            appearance: "outlined",
            borderRadius: "4px",
            color: "outlined",
            ellipsis: true,
            flexGrow: 1,
            padding: "10px 16px",
          })}
          onClick={function () {
            if (router.history.canGoBack()) {
              router.history.back();
            } else {
              window.history.back();
            }
          }}
        >
          {t("%BackToPreviousPage.0%")}
        </button>
        <button
          className={ButtonStyle({
            appearance: "outlined",
            borderRadius: "4px",
            color: "outlined",
            ellipsis: true,
            flexGrow: 1,
            padding: "10px 16px",
          })}
          onClick={function () {
            reset();
          }}
        >
          {t("%Reset.0%")}
        </button>
      </div>
    </div>
  );
}

function NotFoundComponent({ data }: NotFoundRouteProps) {
  const router = useRouter();
  const { t } = useTranslation();
  const { facilityKey } = useParams({ strict: false });

  useEffect(
    function () {
      if (data) {
        console.group("ページが見つかりません。");
        console.debug(data);
        console.groupEnd();
      }
    },
    [data],
  );

  return (
    <div
      className={css({
        alignItems: "center",
        backgroundColor: "#F7F5F2",
        display: "flex",
        flexGrow: 1,
        justifyContent: "center",
        padding: { smDown: "16px", sm: "32px" },
      })}
      data-maximized
    >
      <div className={css(TypographyStyle.raw({ color: "default" }), { maxWidth: "370px" })}>
        <div className={css({ fontSize: "64px", lineHeight: 1, textAlign: "center" })}>🤔</div>
        <h1
          className={TypographyStyle({
            color: "default",
            ellipsis: true,
            fontSize: "36px",
            fontWeight: 700,
            lineHeight: 1,
            marginTop: "20px",
            textAlign: "center",
          })}
        >
          404
        </h1>

        <h2
          className={TypographyStyle({
            ellipsis: true,
            fontSize: "18px",
            fontWeight: 600,
            lineHeight: 1,
            marginTop: "16px",
            textAlign: "center",
          })}
        >
          {t("%PageNotFound.0%")}
        </h2>

        <p
          className={TypographyStyle({
            fontSize: "14px",
            marginTop: "16px",
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
          })}
        >
          {t("%PageNotFound.1%")}
        </p>

        <div
          className={css({
            alignItems: "center",
            display: "grid",
            gap: "8px",
            gridTemplateColumns: "repeat(auto-fit, minmax(0, 1fr))",
            marginTop: "16px",
          })}
        >
          {isFacilityKey(facilityKey) ? (
            <Link
              className={ButtonStyle({
                appearance: "contained",
                borderRadius: "4px",
                color: "primary",
                flexGrow: 1,
                padding: "10px 16px",
              })}
              params={{ facilityKey }}
              to="/facilities/$facilityKey"
            >
              {t("%BackToTopPage.0%")}
            </Link>
          ) : (
            <Link
              className={ButtonStyle({
                appearance: "contained",
                borderRadius: "4px",
                color: "primary",
                flexGrow: 1,
                padding: "10px 16px",
              })}
              to="/login"
            >
              {t("%BackToLoginPage.0%")}
            </Link>
          )}
          <button
            className={ButtonStyle({
              appearance: "outlined",
              borderRadius: "4px",
              color: "outlined",
              ellipsis: true,
              flexGrow: 1,
              padding: "10px 16px",
            })}
            onClick={function () {
              if (router.history.canGoBack()) {
                router.history.back();
              } else {
                window.history.back();
              }
            }}
          >
            {t("%BackToPreviousPage.0%")}
          </button>
        </div>
      </div>
    </div>
  );
}
